import { useState } from "react";
import { CaretDown } from "@phosphor-icons/react";

/**
 * Shared "How it works + FAQs" block rendered at the bottom of every
 * dashboard tool page. Keeps users oriented — what the tool is, how it
 * works, and the common questions they'd have before trusting it with
 * roster-level work.
 */
export default function ToolInfoFooter({ title, steps = [], faqs = [] }) {
  return (
    <div className="mt-10 space-y-6" data-testid="tool-info-footer">
      {/* How it works */}
      {steps.length > 0 && (
        <section className="bg-white rounded-2xl border border-slate-200 p-6">
          <h2 className="font-display font-bold text-lg text-slate-900 mb-4 flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-teal/10 text-teal text-[11px] font-bold">?</span>
            How {title} works
          </h2>
          <ol className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {steps.map((s, i) => (
              <li key={i} className="flex gap-3">
                <span className="shrink-0 w-7 h-7 rounded-full bg-navy text-white text-xs font-bold flex items-center justify-center">
                  {i + 1}
                </span>
                <div>
                  <p className="font-semibold text-sm text-slate-900">{s.title}</p>
                  <p className="text-sm text-slate-500 leading-relaxed mt-0.5">{s.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>
      )}

      {/* FAQ */}
      {faqs.length > 0 && (
        <section className="bg-white rounded-2xl border border-slate-200 p-6">
          <h2 className="font-display font-bold text-lg text-slate-900 mb-4">
            Frequently asked questions
          </h2>
          <div className="divide-y divide-slate-100">
            {faqs.map((f, i) => <FaqRow key={i} q={f.q} a={f.a} />)}
          </div>
        </section>
      )}
    </div>
  );
}

function FaqRow({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="py-3">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between gap-4 text-left group"
        data-testid="faq-toggle"
      >
        <span className="font-medium text-sm text-slate-900 group-hover:text-teal transition-colors">{q}</span>
        <CaretDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="pt-2 pr-8 text-sm text-slate-600 leading-relaxed">
          {a}
        </div>
      )}
    </div>
  );
}
